/**
 * Quote Generator — shared types.
 *
 * Mirrors the `extracted.json` schema + priced-quote model of the OptiClaw quote-gen
 * engine (~/optisigns/os-hubspot-copilot/skills/quote-gen/), with one difference: in
 * staff-app there is no ticket and no LLM extraction step — a human builds
 * `QuoteRequest` directly in the form. The pricing half is identical, deliberately, so
 * a staff quote and an OptiClaw quote for the same request come out to the same cent.
 */

/** The five currencies `deviceProducts.prices` / `productPlans.plans` are keyed by. */
export const CURRENCIES = ['usd', 'eur', 'gbp', 'cad', 'aud'] as const;
export type Currency = (typeof CURRENCIES)[number];

/** `internal` = US domestic. The DB's own key names — not ours to rename. */
export type ShippingRegion = 'internal' | 'international';

/**
 * `addon` is its own kind rather than a flavour of `subscription` because `engage` is both
 * a plan alias and an add-on key — one sku namespace could not tell them apart.
 */
export type LineKind = 'subscription' | 'addon' | 'device' | 'shipping';

/**
 * productPlans term keys we quote at. `monthly` exists in the catalog and is deliberately
 * NOT here: quotes are annual, full stop. Modelling a term we never price only created a
 * path where a line could be silently dropped from a quote the operator thought they'd built.
 */
export type PlanTerm = 'yearly' | 'yearly_freedevice';

// ─────────────────────────────── catalog (from staff-api) ───────────────────────────

export interface CatalogProduct {
  _id: string;
  name?: string;
  description?: string;
  alias?: string;
  variantId?: string;
  /** Legacy USD scalar — used only when `prices[currency]` is absent. */
  price?: number;
  prices?: Record<string, number | null>;
  isKiosk?: boolean;
}

/** `prices[region].{price,priceFirstDevice,additionalPrice}[currency]` */
export interface CatalogShipping {
  _id: string;
  name?: string;
  /**
   * Keyed by region. A region can be present with the value `null` — that is how live
   * `deviceShippings` records "not offered here" (Express `international` is null), so the
   * null must be representable rather than coerced to a missing key.
   */
  prices?: Record<string, ShippingRateNode | null | undefined>;
}

/**
 * A region's rates. Every field is nullable because live `deviceShippings` stores an
 * explicit `null` for a rate that doesn't apply — Standard internal has
 * `priceFirstDevice: null` / `additionalPrice: null` and only a flat `price`. Typing these
 * as merely optional made the real documents unrepresentable.
 */
export interface ShippingRateNode {
  /** Flat per-unit rate, used when there is no first-device/additional split. */
  price?: Record<string, number | null> | null;
  priceFirstDevice?: Record<string, number | null> | null;
  additionalPrice?: Record<string, number | null> | null;
}

/** `plans[currency][term]` */
export interface CatalogPlan {
  _id: string;
  name?: string;
  alias?: string;
  plans?: Record<string, Record<string, PlanTier | undefined> | undefined>;
}

export interface PlanTier {
  /** Per-screen amount in dollars. For `yearly` this is the ANNUAL per-screen price. */
  price?: number | null;
  stripe_plan_id?: string;
  plan?: string;
  discount?: number;
}

/** A tier within an add-on. The tier picks the PRICE KEY a term maps to. */
export interface CatalogAddOnTier {
  /** `standard` | `pro`. */
  key: string;
  name?: string;
  /** e.g. `["pro_monthly","pro_yearly","pro_yearly_freedevice"]`. */
  priceKeys: string[];
}

/**
 * Flattened from `productPlans[*].add_ons` by staff-api.
 *
 * `prices[currency][priceKey]` — note priceKey, not term: a tiered add-on reads
 * `pro_yearly` rather than `yearly`, which is why the tier must be resolved before the
 * price. Getting that wrong quotes OptiSound Standard ($162) for Pro ($270).
 */
export interface CatalogAddOn {
  key: string;
  name?: string;
  /** `screen` (per-screen) | `onetime` (flat charge, quantity meaningless). */
  type?: string;
  order?: number;
  prices?: Record<string, Record<string, PlanTier | undefined> | undefined>;
  /** Billing floor — OptiDev is 3, sold as a "base package up to 3 screens". */
  minQuantity?: number;
  /** Empty when the add-on has no tiers. */
  tiers?: CatalogAddOnTier[];
  /** The tier the portal preselects (`defaultPlan`). */
  defaultTier?: string;
  /** When set, the add-on is ONLY sold in these currencies (OptiSound: usd, cad). */
  limitCurrencies?: string[];
  /**
   * This add-on's non-USD prices come from STRIPE, not Mongo — `prices` holds only `usd`.
   * Four add-ons carry it: engage, brandingCustomDomain, backgroundmusic, optidev.
   */
  isAutoConvertCurrency?: boolean;
  /**
   * Stripe's per-currency amounts, `priceKey → currency → amounts`, in CENTS.
   *
   * CENTS, and deliberately NOT merged into `prices`: everything in `prices` is a decimal
   * the engine scales with `toCents`, so a Stripe amount landing there would be multiplied
   * by 100 — a EUR 205.20 add-on quoted at EUR 20,520. Separate field, separate type.
   */
  stripeRates?: Record<string, Record<string, StripeCurrencyAmount | undefined> | undefined>;
}

/** One currency's Stripe amounts for an add-on, in CENTS. */
export interface StripeCurrencyAmount {
  /** Per-screen rate. */
  perScreenCents?: number;
  /** Tiered prices only — the base package (OptiDev's "$324 up to 3 screens"). */
  baseFlatCents?: number;
}

export interface QuoteCatalog {
  products: CatalogProduct[];
  shippings: CatalogShipping[];
  plans: CatalogPlan[];
  addOns: CatalogAddOn[];
  plansCollection: string;
}

// ─────────────────────────────────── the request ────────────────────────────────────

export interface QuoteCustomer {
  company?: string;
  name?: string;
  email?: string;
  /** Drives the default shipping region AND the billing currency. Blank → US. */
  country?: string;
}

export interface QuoteRequestLine {
  /** Stable row key for React — never read by the engine. */
  id: string;
  kind: LineKind;
  quantity: number | null;
  /** Catalog identifier the operator picked (product `_id` / plan `alias` / shipping `_id`). */
  sku?: string;
  /** Free-text fallback, resolved by exact match + synonyms only. */
  query?: string;
  /** Shipping only — overrides the region inferred from `customer.country`. */
  region?: ShippingRegion;
  /** Subscription and add-on lines. Always annual. */
  term?: PlanTerm;
  /** Add-on only — which tier (`standard` | `pro`). Defaults to the catalog's own. */
  tier?: string;
  /**
   * Percentage off THIS line, 0-100. Subscription and add-on lines only — hardware and
   * shipping are never discounted.
   *
   * Per line rather than per quote because a customer can negotiate different rates on
   * different products: 15% off Pro Plus and 5% off OptiSound is one quote, not two. A
   * single quote-level percentage could only express the case where they happen to match.
   *
   * Held as the RAW STRING the operator is typing, not a number, for the same reason the
   * price field used to be: parsing each keystroke makes "1" un-typeable on the way to "12".
   */
  discountPct?: string | number | null;
}

export interface QuoteRequest {
  currency: Currency | string;
  customer: QuoteCustomer;
  lines: QuoteRequestLine[];
}

// ──────────────────────────────────── the result ────────────────────────────────────

export interface PricedLine {
  /**
   * The `QuoteRequestLine.id` this was priced from, so the editor can pair a form row
   * with its result. Absent on auto-added shipping, which has no originating row.
   *
   * Pairing used to be by `sku`, which silently failed for subscriptions: the plan
   * dropdown emits the ALIAS ("proPlus") while the engine records the `_id`
   * ("5f3f4c76…"), so every subscription row read "unpriced" even though it had priced
   * correctly. Two rows of the same product also both matched the first result.
   */
  rowId?: string;
  kind: LineKind;
  label: string;
  quantity: number;
  sku?: string;
  variantId?: string;
  /** null = could not be priced. Such lines never reach the printed quote. */
  unitCents: number | null;
  amountCents: number | null;
  /** Shipping only. */
  region?: ShippingRegion;
  shippingFirstCents?: number | null;
  autoAdded?: boolean;
  /** Subscription and add-on lines. */
  term?: string;
  /** Add-on only — the tier actually priced, so the quote can name it. */
  tier?: string;
  stripePlanId?: string | null;
  /** True when the currency was missing and a fallback price was used. */
  priceFallback?: boolean;
  /**
   * The percentage applied to this line, and what it came to in cents. Both absent on a
   * line with no discount, so `discountCents` is never a meaningless 0 next to a real pct.
   *
   * `amountCents` stays the LIST amount — quantity x unit. The discount is carried
   * alongside rather than folded in, so the printed quote can show a customer what they
   * were charged and what they saved without having to reconstruct either.
   */
  discountPct?: number;
  discountCents?: number;
}

export interface PricedQuote {
  /** Every line priced AND no flags. */
  ready: boolean;
  /** Human-readable problems. In staff-app these are warnings, not a hard gate. */
  hitl: string[];
  currency: string;
  customer: QuoteCustomer;
  lines: PricedLine[];
  /** Sum of every line's LIST amount, before any discount. */
  subtotalCents: number;
  /** Sum of the per-line discounts. */
  discountCents: number;
  /** Human summary of which lines were discounted, or null when none were. */
  discountNote: string | null;
  /**
   * subtotal − discount. The ONE total, and exactly what the printed quote shows.
   *
   * There used to be a second figure carrying the $25 check fee. The quote does not ask
   * how the customer will pay — the terms bullet states the fee as a condition — so there
   * is nothing to compute it from, and two totals only invited quoting the wrong one.
   */
  totalCents: number;
}
